import React, { useState, useEffect, useMemo } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { adminAPI } from '../../lib/api';
import {
  Calendar, Clock, Users, ChevronLeft, ChevronRight,
  Loader2, Check, X, Settings, Info
} from 'lucide-react';

// Helper to format time to AM/PM
const formatTimeAMPM = (time24) => {
  if (!time24) return '';
  const [hours, minutes] = time24.split(':');
  const h = parseInt(hours);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${minutes} ${ampm}`;
};

// Helper to convert AM/PM to 24h
const parseTimeToValue = (hour, minute, ampm) => {
  let h = parseInt(hour);
  if (ampm === 'PM' && h !== 12) h += 12;
  if (ampm === 'AM' && h === 12) h = 0;
  return `${h.toString().padStart(2, '0')}:${minute}`;
};

export default function TourAvailabilityCalendar({ tourId, tourTitle, onClose }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [availabilityData, setAvailabilityData] = useState({});
  const [blockedDates, setBlockedDates] = useState(new Set());
  
  // Tour settings
  const [settings, setSettings] = useState({
    allDatesAvailable: true,
    maxGuestsPerDay: 20,
    bookingStartTime: '09:00',  // Start time for bookings
    bookingEndTime: '17:00',    // End time for bookings
    bookingWindowDays: 90, // How far in advance can book
  });
  
  // Time picker state for start/end times
  const [startHour, setStartHour] = useState('9');
  const [startMinute, setStartMinute] = useState('00');
  const [startAmPm, setStartAmPm] = useState('AM');
  const [endHour, setEndHour] = useState('5');
  const [endMinute, setEndMinute] = useState('00');
  const [endAmPm, setEndAmPm] = useState('PM');

  useEffect(() => {
    loadAvailability();
  }, [tourId]);

  const loadAvailability = async () => {
    try {
      const response = await adminAPI.getTourAvailability(tourId);
      const slots = response.data?.slots || [];
      const blockedList = response.data?.blocked_dates || [];
      
      // Convert slots to a map by date
      const dataMap = {};
      
      slots.forEach(slot => {
        if (!dataMap[slot.date]) {
          dataMap[slot.date] = {
            times: [],
            maxGuests: slot.max_guests || 20,
            isBlocked: false
          };
        }
        dataMap[slot.date].times.push({
          id: slot.id,
          time: slot.time,
          maxGuests: slot.max_guests,
          booked: slot.booked || slot.booked_guests || 0
        });
      });
      
      setAvailabilityData(dataMap);
      setBlockedDates(new Set(blockedList));
      
      // Load tour settings if available
      if (response.data?.settings) {
        const loadedSettings = response.data.settings;
        setSettings(prev => ({ ...prev, ...loadedSettings }));
        
        // Parse start time for picker
        if (loadedSettings.bookingStartTime) {
          const [h, m] = loadedSettings.bookingStartTime.split(':');
          const hour = parseInt(h);
          setStartHour(String(hour > 12 ? hour - 12 : (hour === 0 ? 12 : hour)));
          setStartMinute(m);
          setStartAmPm(hour >= 12 ? 'PM' : 'AM');
        }
        
        // Parse end time for picker
        if (loadedSettings.bookingEndTime) {
          const [h, m] = loadedSettings.bookingEndTime.split(':');
          const hour = parseInt(h);
          setEndHour(String(hour > 12 ? hour - 12 : (hour === 0 ? 12 : hour)));
          setEndMinute(m);
          setEndAmPm(hour >= 12 ? 'PM' : 'AM');
        }
      }
    } catch (error) {
      console.error('Error loading availability:', error);
      toast.error('Failed to load availability');
    } finally {
      setLoading(false);
    }
  };

  // Generate calendar days for current month
  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startPadding = firstDay.getDay();
    const days = [];
    
    // Previous month padding
    for (let i = 0; i < startPadding; i++) {
      const date = new Date(year, month, -startPadding + i + 1);
      days.push({ date, isCurrentMonth: false });
    }
    
    // Current month days
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }
    
    // Next month padding to fill 6 rows
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
    }
    
    return days;
  }, [currentMonth]);

  const formatDateKey = (date) => {
    return date.toISOString().split('T')[0];
  };

  const isDateAvailable = (date) => {
    const dateKey = formatDateKey(date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Past dates are not available
    if (date < today) return false;
    
    // Check if explicitly blocked
    if (blockedDates.has(dateKey)) return false;
    
    // Check booking window
    const maxDate = new Date(today);
    maxDate.setDate(maxDate.getDate() + settings.bookingWindowDays);
    if (date > maxDate) return false;
    
    // If allDatesAvailable is true, date is available unless blocked
    if (settings.allDatesAvailable) return true;
    
    // Otherwise, check if date has slots
    return !!availabilityData[dateKey]?.times?.length;
  };

  const getDateStatus = (date) => {
    const dateKey = formatDateKey(date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (date < today) return 'past';
    if (blockedDates.has(dateKey)) return 'blocked';
    
    const data = availabilityData[dateKey];
    if (data?.times?.length > 0) {
      const totalBooked = data.times.reduce((sum, t) => sum + (t.booked || 0), 0);
      const totalCapacity = data.times.reduce((sum, t) => sum + (t.maxGuests || settings.maxGuestsPerDay), 0);
      if (totalBooked >= totalCapacity) return 'full';
      if (totalBooked > 0) return 'partial';
      return 'available';
    }
    
    if (settings.allDatesAvailable) return 'available';
    return 'unavailable';
  };

  const toggleDateBlocked = async (date) => {
    const dateKey = formatDateKey(date);
    const isCurrentlyBlocked = blockedDates.has(dateKey);
    
    setSaving(true);
    try {
      if (isCurrentlyBlocked) {
        // Unblock - remove from blocked dates
        await adminAPI.unblockTourDate(tourId, dateKey);
        setBlockedDates(prev => {
          const newSet = new Set(prev);
          newSet.delete(dateKey);
          return newSet;
        });
        toast.success(`${dateKey} is now available`);
      } else {
        // Block the date
        await adminAPI.blockTourDate(tourId, dateKey);
        setBlockedDates(prev => new Set([...prev, dateKey]));
        toast.success(`${dateKey} is now blocked`);
      }
    } catch (error) {
      console.error('Error toggling date:', error);
      toast.error('Failed to update date');
    } finally {
      setSaving(false);
    }
  };

  const handleDateClick = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date < today) return;
    
    setSelectedDate(formatDateKey(date));
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await adminAPI.updateTourAvailabilitySettings(tourId, settings);
      toast.success('Settings saved!');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-bold flex items-center gap-2">
            <Calendar className="h-6 w-6 text-primary" />
            Tour Availability Calendar
          </h3>
          <p className="text-muted-foreground text-sm mt-1">
            Click on dates to manage availability. Green = Available, Red = Blocked
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <Card className="lg:col-span-2 p-4">
          {/* Month Navigation */}
          <div className="flex items-center justify-between mb-4">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <h4 className="font-semibold text-lg">
              {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
            </h4>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          {/* Day Headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {dayNames.map(day => (
              <div key={day} className="text-center text-sm font-medium text-muted-foreground py-2">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1">
            {calendarDays.map((dayInfo, idx) => {
              const dateKey = formatDateKey(dayInfo.date);
              const status = getDateStatus(dayInfo.date);
              const isSelected = selectedDate === dateKey;
              const data = availabilityData[dateKey];
              
              return (
                <button
                  type="button"
                  key={idx}
                  onClick={() => handleDateClick(dayInfo.date)}
                  disabled={status === 'past'}
                  className={`
                    relative p-2 h-16 rounded-lg border-2 transition-all text-left
                    ${!dayInfo.isCurrentMonth ? 'opacity-30' : ''}
                    ${status === 'past' ? 'bg-gray-100 cursor-not-allowed opacity-50' : ''}
                    ${status === 'blocked' ? 'bg-red-100 border-red-300 hover:border-red-400' : ''}
                    ${status === 'available' ? 'bg-green-50 border-green-200 hover:border-green-400' : ''}
                    ${status === 'partial' ? 'bg-yellow-50 border-yellow-200 hover:border-yellow-400' : ''}
                    ${status === 'full' ? 'bg-orange-100 border-orange-300' : ''}
                    ${status === 'unavailable' ? 'bg-gray-50 border-gray-200 hover:border-gray-300' : ''}
                    ${isSelected ? 'ring-2 ring-primary ring-offset-2' : ''}
                  `}
                >
                  <span className={`text-sm font-medium ${status === 'blocked' ? 'text-red-700' : ''}`}>
                    {dayInfo.date.getDate()}
                  </span>
                  
                  {/* Status indicators */}
                  {status === 'blocked' && (
                    <X className="absolute top-1 right-1 h-3 w-3 text-red-500" />
                  )}
                  {status === 'available' && dayInfo.isCurrentMonth && (
                    <Check className="absolute top-1 right-1 h-3 w-3 text-green-500" />
                  )}
                  
                  {/* Booking count */}
                  {data?.times?.length > 0 && (
                    <div className="absolute bottom-1 left-1 right-1">
                      <div className="text-[10px] text-muted-foreground truncate">
                        {data.times.length} slot{data.times.length > 1 ? 's' : ''}
                      </div>
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Legend */}
          <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t">
            <div className="flex items-center gap-2 text-xs">
              <div className="w-4 h-4 rounded bg-green-50 border-2 border-green-200"></div>
              <span>Available</span>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <div className="w-4 h-4 rounded bg-red-100 border-2 border-red-300"></div>
              <span>Blocked</span>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <div className="w-4 h-4 rounded bg-yellow-50 border-2 border-yellow-200"></div>
              <span>Partial Bookings</span>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <div className="w-4 h-4 rounded bg-orange-100 border-2 border-orange-300"></div>
              <span>Fully Booked</span>
            </div>
          </div>
        </Card>

        {/* Right Panel - Settings & Selected Date */}
        <div className="space-y-4">
          {/* Settings Card */}
          <Card className="p-4">
            <h4 className="font-semibold mb-4 flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Availability Settings
            </h4>
            
            <div className="space-y-4">
              {/* All Dates Available Toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm">All Dates Available</Label>
                  <p className="text-xs text-muted-foreground">New dates are available by default</p>
                </div>
                <Switch
                  checked={settings.allDatesAvailable}
                  onCheckedChange={(checked) => setSettings({ ...settings, allDatesAvailable: checked })}
                />
              </div>
              
              {/* Max Guests Per Day */}
              <div>
                <Label className="text-sm">Max Guests Per Day</Label>
                <Input
                  type="number"
                  min="1"
                  value={settings.maxGuestsPerDay}
                  onChange={(e) => setSettings({ ...settings, maxGuestsPerDay: parseInt(e.target.value) || 20 })}
                  className="mt-1"
                />
              </div>
              
              {/* Booking Window */}
              <div>
                <Label className="text-sm">Booking Window (Days)</Label>
                <Input
                  type="number"
                  min="1"
                  max="365"
                  value={settings.bookingWindowDays}
                  onChange={(e) => setSettings({ ...settings, bookingWindowDays: parseInt(e.target.value) || 90 })}
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Customers can book up to {settings.bookingWindowDays} days in advance
                </p>
              </div>
              
              {/* Booking Times */}
              <div>
                <Label className="text-sm font-medium">Booking Times</Label>
                <p className="text-xs text-muted-foreground mb-3">Set the hours customers can book tours</p>
                
                {/* Start Time */}
                <div className="mb-3">
                  <Label className="text-xs text-muted-foreground">Start Time</Label>
                  <div className="flex gap-2 mt-1">
                    <select
                      value={startHour}
                      onChange={(e) => {
                        setStartHour(e.target.value);
                        const time24 = parseTimeToValue(e.target.value, startMinute, startAmPm);
                        setSettings({ ...settings, bookingStartTime: time24 });
                      }}
                      className="border rounded px-2 py-1.5 text-sm flex-1"
                    >
                      {[...Array(12)].map((_, i) => (
                        <option key={i + 1} value={i + 1}>{i + 1}</option>
                      ))}
                    </select>
                    <span className="self-center">:</span>
                    <select
                      value={startMinute}
                      onChange={(e) => {
                        setStartMinute(e.target.value);
                        const time24 = parseTimeToValue(startHour, e.target.value, startAmPm);
                        setSettings({ ...settings, bookingStartTime: time24 });
                      }}
                      className="border rounded px-2 py-1.5 text-sm"
                    >
                      <option value="00">00</option>
                      <option value="15">15</option>
                      <option value="30">30</option>
                      <option value="45">45</option>
                    </select>
                    <select
                      value={startAmPm}
                      onChange={(e) => {
                        setStartAmPm(e.target.value);
                        const time24 = parseTimeToValue(startHour, startMinute, e.target.value);
                        setSettings({ ...settings, bookingStartTime: time24 });
                      }}
                      className="border rounded px-2 py-1.5 text-sm font-medium"
                    >
                      <option value="AM">AM</option>
                      <option value="PM">PM</option>
                    </select>
                  </div>
                </div>
                
                {/* End Time */}
                <div>
                  <Label className="text-xs text-muted-foreground">End Time</Label>
                  <div className="flex gap-2 mt-1">
                    <select
                      value={endHour}
                      onChange={(e) => {
                        setEndHour(e.target.value);
                        const time24 = parseTimeToValue(e.target.value, endMinute, endAmPm);
                        setSettings({ ...settings, bookingEndTime: time24 });
                      }}
                      className="border rounded px-2 py-1.5 text-sm flex-1"
                    >
                      {[...Array(12)].map((_, i) => (
                        <option key={i + 1} value={i + 1}>{i + 1}</option>
                      ))}
                    </select>
                    <span className="self-center">:</span>
                    <select
                      value={endMinute}
                      onChange={(e) => {
                        setEndMinute(e.target.value);
                        const time24 = parseTimeToValue(endHour, e.target.value, endAmPm);
                        setSettings({ ...settings, bookingEndTime: time24 });
                      }}
                      className="border rounded px-2 py-1.5 text-sm"
                    >
                      <option value="00">00</option>
                      <option value="15">15</option>
                      <option value="30">30</option>
                      <option value="45">45</option>
                    </select>
                    <select
                      value={endAmPm}
                      onChange={(e) => {
                        setEndAmPm(e.target.value);
                        const time24 = parseTimeToValue(endHour, endMinute, e.target.value);
                        setSettings({ ...settings, bookingEndTime: time24 });
                      }}
                      className="border rounded px-2 py-1.5 text-sm font-medium"
                    >
                      <option value="AM">AM</option>
                      <option value="PM">PM</option>
                    </select>
                  </div>
                </div>
                
                {/* Preview */}
                <div className="mt-3 p-2 bg-muted rounded-lg">
                  <p className="text-xs text-center text-muted-foreground">
                    Customers can book between <strong className="text-foreground">{formatTimeAMPM(settings.bookingStartTime)}</strong> and <strong className="text-foreground">{formatTimeAMPM(settings.bookingEndTime)}</strong>
                  </p>
                </div>
              </div>
              
              <Button type="button" onClick={saveSettings} disabled={saving} className="w-full">
                {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Save Settings
              </Button>
            </div>
          </Card>

          {/* Selected Date Details */}
          {selectedDate && (
            <Card className="p-4">
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                {new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  month: 'long', 
                  day: 'numeric',
                  year: 'numeric'
                })}
              </h4>
              
              {/* Block/Unblock Button */}
              <Button
                type="button"
                variant={blockedDates.has(selectedDate) ? "default" : "destructive"}
                className="w-full mb-4"
                onClick={() => toggleDateBlocked(new Date(selectedDate + 'T12:00:00'))}
                disabled={saving}
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {blockedDates.has(selectedDate) ? (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Make Available
                  </>
                ) : (
                  <>
                    <X className="h-4 w-4 mr-2" />
                    Block This Date
                  </>
                )}
              </Button>
              
              {/* Date Status */}
              {!blockedDates.has(selectedDate) && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2 text-green-800">
                    <Check className="h-4 w-4" />
                    <span className="font-medium">Available for Booking</span>
                  </div>
                  <p className="text-xs text-green-700 mt-1">
                    Customers can book between {formatTimeAMPM(settings.bookingStartTime)} - {formatTimeAMPM(settings.bookingEndTime)}
                  </p>
                  <p className="text-xs text-green-700 mt-0.5">
                    Max {settings.maxGuestsPerDay} guests per day
                  </p>
                </div>
              )}
              
              {blockedDates.has(selectedDate) && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center gap-2 text-red-800">
                    <X className="h-4 w-4" />
                    <span className="font-medium">Date Blocked</span>
                  </div>
                  <p className="text-xs text-red-700 mt-1">
                    No bookings allowed on this date
                  </p>
                </div>
              )}
            </Card>
          )}
          
          {/* Help Card */}
          <Card className="p-4 bg-blue-50 border-blue-200">
            <h4 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
              <Info className="h-4 w-4" />
              Quick Tips
            </h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Click any date to block/unblock it</li>
              <li>• Green dates are open for booking</li>
              <li>• Red dates are blocked (no bookings)</li>
              <li>• Set booking hours above (Start/End time)</li>
              <li>• Customers can only book within your set hours</li>
            </ul>
          </Card>
        </div>
      </div>
    </div>
  );
}
